import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, TrendingUp, RefreshCw, Loader, Image, Video, Code, FileText, ChevronRight, User, BarChart3, X, Send, Heart, MessageSquare, Plus, HelpCircle, Lightbulb, PenLine, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CommentsModal from './CommentsModal';
import ModelLogo from './ModelLogo';
import { trackEvent, trackPageView } from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * FeedPage - Community Feed with Posts + Benchmark Discussions
 * NEW: Users can write standalone posts (discussions, questions, tips)
 */

// ==========================================
// NEW POST COMPOSER
// ==========================================

function NewPostComposer({ user, onPostCreated, onLoginClick }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('discussion');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const postTypes = [
    { id: 'discussion', label: 'Discussion', icon: <MessageSquare size={14} />, color: 'text-blue-600 bg-blue-50' },
    { id: 'question', label: 'Question', icon: <HelpCircle size={14} />, color: 'text-purple-600 bg-purple-50' },
    { id: 'tip', label: 'Tip / Insight', icon: <Lightbulb size={14} />, color: 'text-amber-600 bg-amber-50' },
  ];

  const handleSubmit = async () => {
    if (!content.trim() || content.trim().length < 3) {
      setError('Post must be at least 3 characters');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/feed/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: content.trim(), post_type: postType })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to create post');
      }

      const data = await res.json();
      setContent('');
      setIsExpanded(false);
      setPostType('discussion');
      trackEvent('Feed', 'Create Post', postType);

      if (onPostCreated) onPostCreated(data.post);
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  // Not logged in - show prompt
  if (!user) {
    return (
      <div
        onClick={onLoginClick}
        className="bg-white rounded-xl border border-gray-200 p-4 mb-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
            <User size={16} className="text-gray-400" />
          </div>
          <span className="text-sm text-gray-400">Sign in to share your thoughts with the community...</span>
        </div>
      </div>
    );
  }

  // Collapsed state
  if (!isExpanded) {
    return (
      <div
        onClick={() => setIsExpanded(true)}
        className="bg-white rounded-xl border border-gray-200 p-4 mb-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
            <User size={14} className="text-white" />
          </div>
          <div className="flex-1 bg-gray-50 rounded-lg px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-100 transition-colors">
            What's on your mind? Share a thought, question, or tip...
          </div>
          <Plus size={20} className="text-blue-500" />
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
            <User size={12} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-800">{user.nickname || user.email?.split('@')[0]}</span>
        </div>
        <button
          onClick={() => { setIsExpanded(false); setContent(''); setError(''); }}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Post Type Selector */}
      <div className="flex gap-2 mb-3">
        {postTypes.map(type => (
          <button
            key={type.id}
            onClick={() => setPostType(type.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              postType === type.id
                ? type.color + ' ring-1 ring-current/20'
                : 'text-gray-500 bg-gray-50 hover:bg-gray-100'
            }`}
          >
            {type.icon}
            {type.label}
          </button>
        ))}
      </div>

      {/* Text Area */}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={
          postType === 'question'
            ? 'Ask the community a question about AI models, benchmarks, or results...'
            : postType === 'tip'
            ? 'Share a useful tip or insight about AI model performance...'
            : 'Share your thoughts with the community...'
        }
        className="w-full min-h-[100px] max-h-[300px] resize-y border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none placeholder:text-gray-400"
        maxLength={2000}
        autoFocus
      />

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{content.length}/2000</span>
          {error && (
            <span className="text-xs text-red-500">{error}</span>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={submitting || !content.trim() || content.trim().length < 3}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader size={14} className="animate-spin" />
              Posting...
            </>
          ) : (
            <>
              <Send size={14} />
              Post
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function ExpandablePostContent({ content, maxLength = 300 }) {
  const [expanded, setExpanded] = useState(false);
  const needsExpansion = content && content.length > maxLength;
  const displayText = expanded || !needsExpansion
    ? content
    : content.substring(0, maxLength) + '...';

  return (
    <>
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
        {displayText || ''}
      </p>
      {needsExpansion && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="mt-1 text-blue-600 hover:text-blue-800 font-medium text-xs inline-flex items-center"
        >
          {expanded ? 'Show less' : 'Show more'}
          <ChevronDown
            size={12}
            className={`ml-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </>
  );
}
// ==========================================
// FEED POST CARD (standalone post)
// ==========================================

function FeedPostCard({ post, user, onLoginClick, onRefresh }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [liked, setLiked] = useState(post.liked_by_me || false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);

  const postTypeConfig = {
    discussion: { icon: <MessageSquare size={12} />, color: 'text-blue-600 bg-blue-50', label: 'Discussion' },
    question: { icon: <HelpCircle size={12} />, color: 'text-purple-600 bg-purple-50', label: 'Question' },
    tip: { icon: <Lightbulb size={12} />, color: 'text-amber-600 bg-amber-50', label: 'Tip' },
  };

  const typeInfo = postTypeConfig[post.post_type] || postTypeConfig.discussion;

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getUserBadge = (tier) => {
    const badges = { 'premium': '👑', 'pro': '⭐', 'starter': '✨' };
    return badges[tier?.toLowerCase()] || null;
  };

  const handleLike = async () => {
    if (!user) {
      onLoginClick?.();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/feed/posts/${post.id}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.liked);
        setLikesCount(prev => data.liked ? prev + 1 : Math.max(prev - 1, 0));
        trackEvent('Feed', data.liked ? 'Like Post' : 'Unlike Post', post.id);
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${API_URL}/feed/posts/${post.id}`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleToggleComments = () => {
    if (!showComments) {
      loadComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !user) return;

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/feed/posts/${post.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newComment.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        setComments(prev => [...prev, data.comment]);
        setNewComment('');
        trackEvent('Feed', 'Comment on Post', post.id);
      }
    } catch (err) {
      console.error('Comment error:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <article className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-all">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
            <User size={12} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-gray-800">{post.username || 'Anonymous'}</span>
              {getUserBadge(post.user_tier) && <span className="text-xs">{getUserBadge(post.user_tier)}</span>}
            </div>
            <span className="text-[10px] text-gray-400">{formatTimeAgo(post.created_at)}</span>
          </div>
        </div>
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeInfo.color}`}>
          {typeInfo.icon}
          <span>{typeInfo.label}</span>
        </div>
      </div>

      {/* Content */}
        <div className="px-4 pb-3">
          <ExpandablePostContent content={post.content} maxLength={300} />
        </div>

      {/* Actions Bar */}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
            liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
          }`}
        >
          <Heart size={14} fill={liked ? 'currentColor' : 'none'} />
          <span>{likesCount > 0 ? likesCount : ''}</span>
        </button>

        <button
          onClick={handleToggleComments}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-blue-500 transition-colors"
        >
          <MessageCircle size={14} />
          <span>{post.comments_count || post.reply_count || 0}</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
          {loadingComments ? (
            <div className="flex items-center justify-center py-4">
              <Loader size={16} className="animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {comments.length > 0 && (
                <div className="space-y-2.5 mb-3">
                  {comments.map((comment, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <User size={10} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-gray-700">{comment.username || 'Anonymous'}</span>
                          {getUserBadge(comment.user_tier) && <span className="text-[10px]">{getUserBadge(comment.user_tier)}</span>}
                          <span className="text-[10px] text-gray-400">{formatTimeAgo(comment.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment */}
              {user ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a reply..."
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none bg-white"
                    maxLength={1000}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={submittingComment || !newComment.trim()}
                    className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                  >
                    {submittingComment ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign in to reply
                </button>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}


// ==========================================
// ALL COMMENTS POPUP (existing - unchanged)
// ==========================================

function AllCommentsPopup({ isOpen, onClose, benchmarkId, prompt }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && benchmarkId) {
      setLoading(true);
      fetch(`${API_URL}/comments/benchmark/${benchmarkId}?limit=50`)
        .then(res => res.json())
        .then(data => {
          setComments(data.comments || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [isOpen, benchmarkId]);

  if (!isOpen) return null;

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getUserBadge = (tier) => {
    const badges = { 'premium': '👑', 'pro': '⭐', 'starter': '✨' };
    return badges[tier?.toLowerCase()] || null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-label="All comments">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900">All Comments ({comments.length})</h3>
            <p className="text-xs text-gray-500 truncate">{prompt}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2" aria-label="Close comments popup">
            <X size={20} className="text-gray-500" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin h-6 w-6 text-blue-500" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No comments yet</p>
          ) : (
            comments.map((comment, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-800">{comment.username || 'Anonymous'}</span>
                    {getUserBadge(comment.user_tier) && <span className="text-sm">{getUserBadge(comment.user_tier)}</span>}
                    <span className="text-xs text-gray-400">{formatTimeAgo(comment.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 break-words">{comment.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Category colors for left border strip
const categoryColors = {
  'IMAGE_GENERATION': 'from-purple-500 to-pink-500',
  'IMAGE_TO_VIDEO': 'from-pink-500 to-rose-500',
  'VIDEO_GENERATION': 'from-pink-500 to-rose-500',
  'TEXT_TO_VIDEO': 'from-pink-500 to-rose-500',
  'CODE_GENERATION': 'from-green-500 to-emerald-500',
  'TEXT_SUMMARIZATION': 'from-blue-500 to-cyan-500',
  'MULTILINGUAL_TRANSLATION': 'from-amber-500 to-orange-500',
  'DATA_ANALYSIS': 'from-indigo-500 to-purple-500',
};

const getCategoryIcon = (category, size = 14) => {
  const icons = {
    'IMAGE_GENERATION': <Image size={size} className="text-purple-500" />,
    'IMAGE_TO_VIDEO': <Video size={size} className="text-pink-500" />,
    'VIDEO_GENERATION': <Video size={size} className="text-pink-500" />,
    'TEXT_TO_VIDEO': <Video size={size} className="text-pink-500" />,
    'CODE_GENERATION': <Code size={size} className="text-green-500" />,
  };
  return icons[category] || <FileText size={size} className="text-blue-500" />;
};

const formatCategory = (category) => {
  if (!category) return 'Unknown';
  return category.replace(/_/g, ' ');
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const getUserBadge = (tier) => {
  const badges = {
    'premium': '👑',
    'pro': '⭐',
    'starter': '✨'
  };
  return badges[tier?.toLowerCase()] || null;
};

function ExpandablePrompt({ prompt, maxLength = 150 }) {
  const [expanded, setExpanded] = useState(false);
  const needsExpansion = prompt && prompt.length > maxLength;

  const displayText = expanded || !needsExpansion
    ? prompt
    : prompt.substring(0, maxLength) + '...';

  return (
    <p className="text-sm text-gray-700 leading-relaxed">
      {displayText || 'No prompt provided'}
      {needsExpansion && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="ml-2 text-blue-600 hover:text-blue-800 font-medium text-xs inline-flex items-center"
        >
          {expanded ? 'Show less' : 'Show more'}
          <ChevronRight
            size={12}
            className={`ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      )}
    </p>
  );
}

function MiniResultsStacked({ results, category }) {
  if (!results || results.length === 0) {
    return (
      <div className="flex items-center justify-center h-16 bg-gray-50 rounded text-gray-400 text-[10px]">
        No preview
      </div>
    );
  }

  const isImageGen = category === 'IMAGE_GENERATION';
  const isVideoGen = ['IMAGE_TO_VIDEO', 'VIDEO_GENERATION', 'TEXT_TO_VIDEO'].includes(category);

  return (
    <div className="flex flex-col gap-1">
      {results.slice(0, 2).map((result, idx) => (
        <div key={idx} className="relative">
          <div className="absolute top-0.5 left-0.5 z-10 flex items-center gap-0.5 bg-black/70 text-white text-[7px] px-1 py-0.5 rounded">
            <ModelLogo modelName={result.model_name || result.model_id} size="xs" className="!w-2.5 !h-2.5" />
            <span className="truncate max-w-[45px]">{result.model_name || 'Model'}</span>
          </div>

          {isImageGen && result.image_url ? (
            <img
              src={result.image_url}
              alt={`AI result by ${result.model_name || 'model'}`}
              className="w-full aspect-square object-cover rounded border border-gray-200"
              loading="lazy"
              decoding="async"
              width={112}
              height={112}
            />
          ) : isVideoGen ? (
            <div className="w-full aspect-square bg-gray-900 rounded flex items-center justify-center">
              <Video size={14} className="text-gray-500" aria-hidden="true" />
            </div>
          ) : (
            <div className="w-full aspect-square bg-gray-100 rounded p-1 overflow-hidden">
              <p className="text-[7px] text-gray-500 line-clamp-3">
                {(result.output || result.response || '').substring(0, 50)}...
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ==========================================
// MAIN FEED PAGE
// ==========================================

function FeedPage({ user, onOpenLogin }) {
  const navigate = useNavigate();
  const [discussions, setDiscussions] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [commentsModal, setCommentsModal] = useState(null);
  const [commentCounts, setCommentCounts] = useState({});
  const [allCommentsPopup, setAllCommentsPopup] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'posts', 'benchmarks'

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  const viewFullResults = (e, benchmarkId) => {
    e.stopPropagation();
    trackEvent('Feed', 'View Stats', benchmarkId);
    navigate(`/?benchmark=${benchmarkId}`);
  };

  useEffect(() => {
    trackPageView('/feed', 'Community Feed');
  }, []);

  // Load feed posts (standalone)
  const loadFeedPosts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/feed/posts?limit=${LIMIT}&offset=0`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFeedPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Error loading feed posts:', err);
    }
  }, []);

  // Load benchmark discussions (existing)
  const loadFeed = useCallback(async (showRefresh = false, isLoadMore = false, currentOffset = 0) => {
    if (showRefresh) {
      setRefreshing(true);
    } else if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const feedResponse = await fetch(`${API_URL}/comments/feed?limit=${LIMIT}&offset=${currentOffset}`);
      if (!feedResponse.ok) throw new Error('Failed to load feed');

      const feedData = await feedResponse.json();
      const activeDiscussions = feedData.active_discussions || [];

      setHasMore(activeDiscussions.length === LIMIT);

      const benchmarkMap = {};
      activeDiscussions.forEach(item => {
        const bid = item.benchmark_id;
        if (!benchmarkMap[bid]) {
          benchmarkMap[bid] = {
            benchmark_id: bid,
            category: item.category,
            prompt: item.prompt,
            created_at: item.created_at,
            results: item.results || [],
            comment_count: item.comment_count || 0,
            allComments: [item],
            latest_comment: item
          };
        } else {
          benchmarkMap[bid].allComments.push(item);
          const existing = new Date(benchmarkMap[bid].latest_comment?.created_at || 0);
          const current = new Date(item.created_at || 0);
          if (current > existing) {
            benchmarkMap[bid].latest_comment = item;
          }
        }
      });

      const sortedDiscussions = Object.values(benchmarkMap)
        .map(d => ({
          ...d,
          allComments: d.allComments.sort((a, b) =>
            new Date(b.created_at || 0) - new Date(a.created_at || 0)
          )
        }))
        .sort((a, b) => {
          const aTime = new Date(a.latest_comment?.created_at || 0);
          const bTime = new Date(b.latest_comment?.created_at || 0);
          return bTime - aTime;
        });

      if (isLoadMore) {
        setDiscussions(prev => {
          const existingIds = new Set(prev.map(d => d.benchmark_id));
          const newDiscussions = sortedDiscussions.filter(d => !existingIds.has(d.benchmark_id));
          return [...prev, ...newDiscussions];
        });
        setOffset(currentOffset + LIMIT);
      } else {
        setDiscussions(sortedDiscussions);
        setOffset(LIMIT);
      }

      setCommentCounts(prev => {
        const counts = isLoadMore ? { ...prev } : {};
        sortedDiscussions.forEach(d => {
          counts[d.benchmark_id] = d.comment_count;
        });
        return counts;
      });

    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadFeed(false, true, offset);
    }
  }, [loadingMore, hasMore, offset, loadFeed]);

  // Initial load
  useEffect(() => {
    Promise.all([
      loadFeed(false, false, 0),
      loadFeedPosts()
    ]);
  }, [loadFeed, loadFeedPosts]);

  const handleOpenComments = (benchmarkId, category, prompt) => {
    trackEvent('Feed', 'Open Comments Modal', benchmarkId);
    setCommentsModal({
      benchmarkId,
      info: { category, prompt }
    });
  };

  const handleCloseModal = () => {
    setCommentsModal(null);
    setOffset(0);
    loadFeed(true, false, 0);
  };

  const handleRefresh = () => {
    trackEvent('Feed', 'Refresh');
    setOffset(0);
    loadFeed(true, false, 0);
    loadFeedPosts();
  };

  const handlePostCreated = (newPost) => {
    setFeedPosts(prev => [newPost, ...prev]);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-3">
        <div className="flex flex-col items-center justify-center py-20">
          <Loader className="animate-spin h-12 w-12 text-blue-600 mb-4" />
          <p className="text-gray-600">Loading community feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-3">

      {/* New Post Composer */}
      <NewPostComposer
        user={user}
        onPostCreated={handlePostCreated}
        onLoginClick={onOpenLogin}
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 mb-4 bg-gray-100 rounded-lg p-1">
        {[
          { id: 'all', label: 'All', icon: <TrendingUp size={14} /> },
          { id: 'posts', label: 'Posts', icon: <PenLine size={14} /> },
          { id: 'benchmarks', label: 'Benchmarks', icon: <BarChart3 size={14} /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}

        {/* Refresh button */}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
          aria-label="Refresh feed"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Feed Content */}
      <section>
        {/* Standalone Posts (show when tab is 'all' or 'posts') */}
        {(activeTab === 'all' || activeTab === 'posts') && feedPosts.length > 0 && (
          <div className="space-y-3 mb-4">
            {feedPosts.map(post => (
              <FeedPostCard
                key={post.id}
                post={post}
                user={user}
                onLoginClick={onOpenLogin}
                onRefresh={handleRefresh}
              />
            ))}
          </div>
        )}

        {/* Benchmark Discussions (show when tab is 'all' or 'benchmarks') */}
        {(activeTab === 'all' || activeTab === 'benchmarks') && (
          <>
            {activeTab === 'all' && feedPosts.length > 0 && discussions.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">Benchmark Discussions</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
            )}

            {discussions.length === 0 && feedPosts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <MessageCircle size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No discussions yet</h3>
                <p className="text-gray-500">Be the first to start a discussion!</p>
              </div>
            ) : discussions.length > 0 ? (
              <>
              <div className="space-y-3">
                {discussions.map((discussion) => {
                  const count = commentCounts[discussion.benchmark_id] || 0;
                  const latestComment = discussion.latest_comment;
                  const colorGradient = categoryColors[discussion.category] || 'from-blue-500 to-cyan-500';

                  const commentsToShow = discussion.allComments?.slice(0, 3) || (latestComment ? [latestComment] : []);
                  const hasMoreComments = count > commentsToShow.length;

                  return (
                    <article
                      key={discussion.benchmark_id}
                      onClick={() => handleOpenComments(discussion.benchmark_id, discussion.category, discussion.prompt)}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                      role="button"
                      tabIndex={0}
                      aria-label={`Discussion about ${formatCategory(discussion.category)} with ${count} comments. Click to view.`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenComments(discussion.benchmark_id, discussion.category, discussion.prompt);
                        }
                      }}
                    >
                      {/* Header with Stats button */}
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-1 h-6 rounded-full bg-gradient-to-b ${colorGradient}`} aria-hidden="true" />
                          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 rounded">
                            {getCategoryIcon(discussion.category, 12)}
                            <span className="text-[10px] font-semibold text-gray-600 uppercase">
                              {formatCategory(discussion.category)}
                            </span>
                          </div>
                          <span className="text-[10px] text-gray-400">
                            {formatTimeAgo(latestComment?.created_at || discussion.created_at)}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => viewFullResults(e, discussion.benchmark_id)}
                            className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg text-xs font-medium transition-colors border border-gray-200"
                            aria-label="View full statistics and comparison details"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <rect x="4" y="14" width="4" height="6" rx="1" fill="#F87171"/>
                              <rect x="10" y="10" width="4" height="10" rx="1" fill="#60A5FA"/>
                              <rect x="16" y="6" width="4" height="14" rx="1" fill="#4ADE80"/>
                            </svg>
                            <span>Stats</span>
                          </button>

                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded" aria-label={`${count} comments`}>
                            <MessageCircle size={12} aria-hidden="true" />
                            <span className="text-xs font-semibold">{count}</span>
                          </div>
                        </div>
                      </div>

                      {/* Prompt */}
                      <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
                        <ExpandablePrompt prompt={discussion.prompt} maxLength={80} />
                      </div>

                      {/* Main Content: Images Left | Comments Right */}
                      <div className="flex">
                        <div className="w-24 sm:w-28 p-1.5 border-r border-gray-100 flex flex-col gap-1">
                          <MiniResultsStacked results={discussion.results} category={discussion.category} />
                        </div>

                        <div className="flex-1 p-3">
                          {commentsToShow.length > 0 ? (
                            <div className="space-y-2">
                              {commentsToShow.map((comment, idx) => (
                                <div key={idx} className="flex items-start gap-2">
                                  <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <User size={10} className="text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-xs font-semibold text-gray-800">
                                        {comment.username || 'Anonymous'}
                                      </span>
                                      {getUserBadge(comment.user_tier) && (
                                        <span className="text-xs">{getUserBadge(comment.user_tier)}</span>
                                      )}
                                      <span className="text-[10px] text-gray-400">
                                        {formatTimeAgo(comment.created_at)}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">
                                      {comment.content}
                                    </p>
                                  </div>
                                </div>
                              ))}

                              {hasMoreComments && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    trackEvent('Feed', 'View All Comments', discussion.benchmark_id);
                                    setAllCommentsPopup({
                                      benchmarkId: discussion.benchmark_id,
                                      prompt: discussion.prompt
                                    });
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 ml-8"
                                  aria-label={`View all ${count} comments on this comparison`}
                                >
                                  <span>View all {count} comments</span>
                                  <ChevronRight size={12} aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                              <MessageCircle size={16} className="mr-2" aria-hidden="true" />
                              Be first to comment
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                    aria-label="Load more discussions"
                    aria-busy={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <Loader size={16} className="animate-spin" aria-hidden="true" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <ChevronRight size={16} className="rotate-90" aria-hidden="true" />
                        <span>Load More</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {!hasMore && discussions.length > 0 && (
                <div className="text-center mt-6 text-gray-400 text-sm">
                  You've reached the end of the feed
                </div>
              )}
              </>
            ) : null}
          </>
        )}

        {/* Empty posts tab */}
        {activeTab === 'posts' && feedPosts.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <PenLine size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
            <p className="text-gray-500">Be the first to share something with the community!</p>
          </div>
        )}
      </section>

      {/* Comments Modal */}
      {commentsModal && (
        <CommentsModal
          isOpen={!!commentsModal}
          onClose={handleCloseModal}
          benchmarkId={commentsModal.benchmarkId}
          benchmarkInfo={commentsModal.info}
          user={user}
          onLoginClick={onOpenLogin}
        />
      )}

      {/* All Comments Popup */}
      {allCommentsPopup && (
        <AllCommentsPopup
          isOpen={!!allCommentsPopup}
          onClose={() => setAllCommentsPopup(null)}
          benchmarkId={allCommentsPopup.benchmarkId}
          prompt={allCommentsPopup.prompt}
        />
      )}
    </div>
  );
}

export default FeedPage;